/**
 * Shared reload-persistence mechanism for optimistic like/follow overlays.
 *
 * The bf-gql normalized-cache overlay (`optimisticUpdate`) repaints every
 * mounted button in the same tick but persists to async IndexedDB, which loses
 * the read-after-write race on reload — the stale gateway `liked`/`state`/
 * `isFollowed` echo lands first. The durable record therefore lives in a
 * synchronous localStorage-backed {@link LocalOverlay}; on boot
 * {@link PersistedOptimisticOverlay.reapplyPersisted} re-arms the in-memory
 * cache overlay from it (with the ENTRY's remaining TTL, so a re-applied
 * override still heals to the server value at its original deadline).
 *
 * Track likes, album library membership and artist follows all share this
 * primitive — the only per-domain differences are the persistence key, the
 * value schema and the normalized-cache entity shape.
 */

import { optimisticUpdate, type CacheableEntity } from '@amzn/bf-gql'
import { createPersistentItem } from '@amazon/vinyl-util'
import {
    createLocalOverlay,
    type LocalOverlay,
    type OverlayValueValidator,
} from '@/util/localOverlay'
import { registerSignOutClear } from '@/signOutOverlays'

// A just-made like/follow out-ranks the gateway's stale read-after-write echo
// for this window. Matches the bf-gql overlay ceiling
// (MAX_OPTIMISTIC_UPDATE_TIME_SECONDS, which `optimisticUpdate` clamps to) and
// Firefly's api-cache read-after-write window.
export const OPTIMISTIC_OVERLAY_TTL_SECONDS = 600

// Members are arrow-valued function properties (not methods): each closes over
// its overlay, never `this`, so callers can detach them (e.g.
// `export const reapplyPersistedTrackLikes = overlay.reapplyPersisted`).
export interface PersistedOptimisticOverlay<V> {
    /** Remember `value` for `id` durably AND re-arm the cache overlay this tick. */
    readonly write: (id: string, value: V) => void
    /**
     * Re-arm the cache from persisted entries on boot, each with its REMAINING
     * TTL. `overlay`/`now` are injectable for tests; production passes neither.
     */
    readonly reapplyPersisted: (
        overlay?: LocalOverlay<string, V>,
        now?: () => number
    ) => Promise<void>
    /** Drop every persisted + in-memory entry (sign-out). */
    readonly clearAll: () => void
}

export interface PersistedOptimisticOverlayConfig<V> {
    /** localStorage key for the persisted snapshot. */
    readonly persistenceKey: string
    /** Rejects hydrated values written by an obsolete schema. */
    readonly validateValue: OverlayValueValidator<V>
    /** Build the normalized-cache entity that overlays `value` for `id`. */
    readonly buildEntity: (id: string, value: V) => CacheableEntity
}

/**
 * Create a persisted optimistic overlay and auto-register its `clearAll` with
 * the sign-out seam, so every overlay built here clears on sign-out with no
 * per-service wiring and no import cycle (see {@link registerSignOutClear}).
 */
export function createPersistedOptimisticOverlay<V>(
    config: PersistedOptimisticOverlayConfig<V>
): PersistedOptimisticOverlay<V> {
    const overlay = createLocalOverlay<string, V>({
        persistence: createPersistentItem({ key: config.persistenceKey, defaultValue: {} }),
        validateValue: config.validateValue,
    })

    // In-memory cache overlay only — durability is this module's LocalOverlay,
    // so skip bf-gql's async IndexedDB write (`persist: false`).
    const applyToCache = (id: string, value: V, seconds: number): void => {
        optimisticUpdate(config.buildEntity(id, value), { time: seconds, persist: false })
    }

    const api: PersistedOptimisticOverlay<V> = {
        write: (id, value) => {
            overlay.write(id, value, { ttlMs: OPTIMISTIC_OVERLAY_TTL_SECONDS * 1000 })
            applyToCache(id, value, OPTIMISTIC_OVERLAY_TTL_SECONDS)
        },
        reapplyPersisted: async (from = overlay, now = Date.now) => {
            await from.whenHydrated()
            for (const { key, value, expiresAt } of from.liveEntries()) {
                const remainingSeconds = (expiresAt - now()) / 1000
                if (remainingSeconds > 0) applyToCache(key, value, remainingSeconds)
            }
        },
        clearAll: () => overlay.clearAll(),
    }

    registerSignOutClear(api.clearAll)
    return api
}
